import { useSelector } from '@xstate/react';
import { useFormContext } from 'react-hook-form';

import { SimpleTooltip } from '@eluve/components';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  FormControl,
  FormField,
  FormItem,
  Icon,
  Input,
  Stack,
  cn,
} from '@eluve/components';
import { useTranslation } from '@eluve/i18n';

import { BlockTypeIcon } from './BlockTypeIcon';
import { templateBuilderStore } from './templateBuilderStore';
import { FlattenedBlock } from './utilities';

interface TreeItemHeaderProps {
  handleProps?: React.HTMLAttributes<HTMLDivElement>;
  collapsed?: boolean;
  block: FlattenedBlock;
}

export const TreeItemHeader: React.FC<TreeItemHeaderProps> = ({
  handleProps,
  collapsed,
  block,
}) => {
  const { t } = useTranslation();
  const { control } = useFormContext();

  const labelPlaceholder: Partial<Record<FlattenedBlock['type'], string>> = {
    group: t('smartBlocks.templateBuilder.groupNamePlaceholder'),
    text: t('smartBlocks.templateBuilder.textBlockNamePlaceholder'),
    checkbox: t('smartBlocks.templateBuilder.checkboxNamePlaceholder'),
  };

  const isReadonly = useSelector(
    templateBuilderStore,
    (store) => store.context.isReadOnly,
  );

  const isImported = useSelector(
    templateBuilderStore,
    (store) => store.context.isImported,
  );

  return (
    <Stack
      className="border-b bg-bg-secondary p-5"
      justify="between"
      gap={4}
      direction="horizontal"
    >
      <Stack gap={4} direction="horizontal">
        {!isReadonly && !isImported && (
          <div {...handleProps}>
            <Icon name="GripVertical" />
          </div>
        )}
        <SimpleTooltip
          label={
            collapsed
              ? t('smartBlocks.templateBuilder.expand')
              : t('smartBlocks.templateBuilder.collapse')
          }
        >
          <Button
            variant="outline"
            onClick={() =>
              templateBuilderStore.send({
                type: 'toggleCollapse',
                id: block.id,
                collapsed: !collapsed,
              })
            }
          >
            <Icon
              name={!collapsed ? 'ChevronsDownUp' : 'ChevronsUpDown'}
              size="auto"
            />
          </Button>
        </SimpleTooltip>
        <BlockTypeIcon type={block.type} />
        <FormField
          control={control}
          name="label"
          render={({ field, fieldState: { error } }) => (
            <FormItem className="flex-1">
              <FormControl>
                <Input
                  placeholder={labelPlaceholder[block.type]}
                  {...field}
                  disabled={isReadonly || isImported}
                  className={cn(error && 'border-border-negative-strong')}
                />
              </FormControl>
            </FormItem>
          )}
        />
      </Stack>
      {!isReadonly && !isImported && (
        <AlertDialog>
          <SimpleTooltip label={t('smartBlocks.templateBuilder.removeBlock')}>
            <AlertDialogTrigger asChild>
              <Button variant="outline">
                <Icon name="Trash" size="auto" />
              </Button>
            </AlertDialogTrigger>
          </SimpleTooltip>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t('smartBlocks.templateBuilder.confirmRemoveTitle')}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t('smartBlocks.templateBuilder.confirmRemoveDescription')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
              <AlertDialogAction
                variant="destructive"
                onClick={() =>
                  templateBuilderStore.send({
                    type: 'removeBlock',
                    id: block.id,
                  })
                }
              >
                {t('smartBlocks.templateBuilder.remove')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </Stack>
  );
};
